/**
 * New offboarding form (Phase 2c; canvas OffboardingScreen in edit mode). Built from the shared parts in fields.ts:
 * the leaver, manager and two delegate people pickers (GET /api/directory/users), person type radios, the
 * department select (pre-filled from the leaver's directory department when it is a listed one), the last day +
 * an access-termination time in 15-minute steps (default 2:00 PM Central, the canvas banner kept), mailbox and
 * OneDrive access toggles with a picker each, the equipment boxes (+ Scanner, new in 2c) with the Other
 * description, and "Skip 72-hr hold" for a Super Admin only.
 *
 * The leaver's Intune devices are shown read-only under the equipment boxes once a leaver is picked (devices.ts):
 * nothing is pre-ticked from them and nothing from them is stored.
 *
 * Validation is the API's own module, run here before the POST and again, authoritatively, on the server; a 400 is
 * mapped onto the fields it names, a 409 duplicate becomes a confirm. No "Licenses to remove" (ADR 058 §7.6).
 */
import { html, nothing, type TemplateResult } from 'lit-html';
import { ApiError, createOffboarding, getDepartments } from './api';
import type { App } from './app';
import { clearDevices, devicesPanel, lookupDevices, newDevices } from './devices';
import { checked, field, invalid, newPeople, peoplePicker, radioGroup, switchRow, val, type FormCtx, type Person } from './fields';
import { instant } from './format';
import {
  addDays,
  centralToUtc,
  centralToday,
  DEFAULT_TERMINATION_TIME,
  isIsoDate,
  MAX_START_DAYS,
  OFFBOARDING_EQUIPMENT,
  PERSON_TYPES,
  SOFT_MESSAGES,
  terminationTimes,
  validateOffboarding,
  type FieldError,
  type OffboardingDraft,
} from '../../api/src/requests/validation';

// ---- state ------------------------------------------------------------------------------------------------------

function blank() {
  return {
    ready: false,
    loadError: '',
    departments: [] as string[],
    personType: '',
    department: '',
    leaver: newPeople(),
    manager: newPeople(),
    lastDay: centralToday(),
    terminationTime: DEFAULT_TERMINATION_TIME,
    emailAccessNeeded: false,
    emailWho: newPeople(),
    onedriveAccessNeeded: false,
    onedriveWho: newPeople(),
    equipment: new Set<string>(),
    equipOther: false,
    equipOtherDescription: '',
    forceStart: false,
    devices: newDevices(),
    errors: [] as FieldError[],
    submitting: false,
    submitError: '',
    dup: null as null | { message: string; matches: number[]; hiddenMatches: number },
  };
}

let s = blank();

export function enterOffboarding(app: App): void {
  clearDevices(s.devices);
  s = blank();
  // Canvas OnVisible L18: a new request defaults the manager to the person filling it in.
  s.manager.sel = { displayName: app.me.name, email: app.me.upn.toLowerCase() };
  void load(app);
}

async function load(app: App): Promise<void> {
  try {
    const departments = await getDepartments();
    s.departments = departments.filter((d) => d.active).map((d) => d.name);
    s.ready = true;
  } catch (e) {
    s.loadError = (e as Error).message || 'The form could not be loaded.';
  }
  app.redraw();
}

// ---- helpers ----------------------------------------------------------------------------------------------------

const errorsFor = (field: string) => s.errors.filter((x) => x.field === field).map((x) => x.message);

function touch(app: App, ...fields: string[]): void {
  const before = s.errors.length;
  s.errors = s.errors.filter((x) => !fields.includes(x.field));
  if (s.errors.length !== before) app.redraw();
}

const cx = (app: App): FormCtx => ({ app, errors: () => s.errors, touch: (...fields) => touch(app, ...fields) });

function draft(confirmDuplicate = false): OffboardingDraft {
  return {
    personType: s.personType,
    department: s.department,
    employeeEmail: s.leaver.sel?.email ?? '',
    managerEmail: s.manager.sel?.email ?? '',
    lastDay: s.lastDay,
    terminationTime: s.terminationTime,
    emailAccessNeeded: s.emailAccessNeeded,
    emailAccessRecipient: s.emailAccessNeeded ? (s.emailWho.sel?.email ?? '') : '',
    onedriveAccessNeeded: s.onedriveAccessNeeded,
    onedriveAccessRecipient: s.onedriveAccessNeeded ? (s.onedriveWho.sel?.email ?? '') : '',
    equipment: [...s.equipment],
    equipOther: s.equipOther,
    equipOtherDescription: s.equipOther ? s.equipOtherDescription : '',
    forceStart: s.forceStart,
    confirmDuplicate,
  };
}

/** The leaver was picked: pre-fill the department from the directory when it is a listed one, otherwise clear it
 *  (2d tidy, 2c open item 6: a previous leaver's department must not carry over), and look up devices. */
function onLeaver(app: App, p: Person): void {
  s.department = p.department && s.departments.includes(p.department) ? p.department : '';
  touch(app, 'department');
  // Devices: reporting, not form logic. One lookup per pick; a re-pick supersedes the previous one.
  lookupDevices(app, s.devices, p.id || p.email, p.displayName);
}

/** "2:00 PM" for the time select's options. */
function timeLabel(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

/** The soft, non-blocking line under the time: the chosen termination instant is already past. */
function terminationSoft(): string | undefined {
  if (!isIsoDate(s.lastDay)) return undefined;
  try {
    return new Date(centralToUtc(s.lastDay, s.terminationTime)).getTime() < Date.now() ? SOFT_MESSAGES.terminationPast : undefined;
  } catch {
    return undefined;
  }
}

// ---- submit -----------------------------------------------------------------------------------------------------

async function submit(app: App, confirmDuplicate = false): Promise<void> {
  const d = draft(confirmDuplicate);
  s.submitError = '';
  s.dup = null;
  s.errors = validateOffboarding(d, { today: centralToday(), departments: s.departments }).errors;
  if (s.errors.length) {
    app.redraw();
    requestAnimationFrame(() => document.getElementById('form-errors')?.focus());
    return;
  }
  s.submitting = true;
  app.redraw();
  try {
    const r = await createOffboarding(d);
    app.notify(`Request saved: ${r.displayName}`);
    app.go(`#/requests/${r.id}`);
    return;
  } catch (e) {
    if (e instanceof ApiError && e.status === 409) {
      s.dup = {
        message: String(e.body.message ?? e.message),
        matches: (e.body.matches as number[] | undefined) ?? [],
        hiddenMatches: Number(e.body.hiddenMatches ?? 0),
      };
    } else if (e instanceof ApiError && e.status === 400 && e.fieldErrors.length) {
      s.errors = e.fieldErrors;
      requestAnimationFrame(() => document.getElementById('form-errors')?.focus());
    } else {
      s.submitError = e instanceof ApiError ? e.message : (e as Error).message || 'The request could not be saved.';
    }
  } finally {
    s.submitting = false;
  }
  app.redraw();
}

function duplicateDialog(app: App): TemplateResult {
  if (!s.dup) return html``;
  const dup = s.dup;
  return html`
    <div class="modal-scrim">
      <div class="modal card" role="dialog" aria-modal="true" aria-labelledby="dup-title">
        <h2 id="dup-title">Possible duplicate</h2>
        <p>${dup.message}</p>
        ${dup.matches.length
          ? html`<p class="muted">Existing: ${dup.matches.map(
              (id, i) => html`${i ? ', ' : ''}<a href="#/requests/${id}" target="_blank" rel="noopener">request #${id}</a>`,
            )}</p>`
          : nothing}
        <div class="modal-acts">
          <button type="button" class="btn" @click=${() => submit(app, true)}>Continue</button>
          <button type="button" class="btn ghost" @click=${() => {
            s.dup = null;
            app.redraw();
          }}>Go back</button>
        </div>
      </div>
    </div>`;
}

// ---- view -------------------------------------------------------------------------------------------------------

/** Box labels (canvas L1752-2039 wording, "Keys (office / cubicle)" per the 2c prompt); values are the enum labels. */
const EQUIPMENT_LABELS: Record<string, string> = { Keys: 'Keys (office / cubicle)' };
const TIMES = terminationTimes();

export function offboardingView(app: App): TemplateResult {
  if (s.loadError) {
    return html`<main id="main" class="wrap">
      <p><a class="back" href="#/">← Requests</a></p>
      <div class="notice err" role="alert">${s.loadError}</div>
    </main>`;
  }
  if (!s.ready) {
    return html`<main id="main" class="wrap"><p><a class="back" href="#/">← Requests</a></p><div class="card"><p>Loading the form…</p></div></main>`;
  }
  const c = cx(app);
  const today = centralToday();
  const soft = terminationSoft();
  const leaverName = s.leaver.sel?.displayName;
  return html`
    <main id="main" class="wrap form-page">
      <p><a class="back" href="#/">← Requests</a></p>
      <div class="hello"><h1>New offboarding request</h1></div>

      ${s.errors.length
        ? html`<div class="notice err" id="form-errors" tabindex="-1" role="alert">
            <b>Please fix the following before saving:</b>
            <ul>${s.errors.map((e) => html`<li>${e.message}</li>`)}</ul>
          </div>`
        : nothing}
      ${s.submitError ? html`<div class="notice err" role="alert">${s.submitError}</div>` : nothing}

      <form novalidate @submit=${(e: Event) => {
        e.preventDefault();
        void submit(app);
      }}>
        <section class="card">
          <h3>Leaver</h3>
          <div class="notice banner">Note: Laptop, phone, email, and Salesforce access terminate at 2:00 PM on last day unless a different time is entered below.</div>
          <div class="grid2">
            ${peoplePicker(c, 'leaver', 'Employee account', s.leaver, 'employeeEmail', true, 'Search the M365 directory for the person leaving.', (p) => onLeaver(app, p))}
            ${radioGroup(c, 'personType', 'Person type', PERSON_TYPES, s.personType, (v) => (s.personType = v))}
            ${field(
              c,
              'department',
              'Department',
              html`<select id="department" aria-invalid=${invalid(c, 'department')} @change=${(e: Event) => {
                s.department = val(e);
                touch(app, 'department');
              }}>
                <option value="" ?selected=${!s.department}>Choose a department</option>
                ${s.departments.map((d) => html`<option value=${d} ?selected=${s.department === d}>${d}</option>`)}
              </select>`,
              { required: true, hint: 'Pre-filled from the directory when the leaver is picked; change it if needed.' },
            )}
            ${peoplePicker(c, 'manager', 'Manager', s.manager, 'managerEmail', true, 'Defaults to you. Search the directory to pick someone else.')}
          </div>
        </section>

        <section class="card">
          <h3>Access termination</h3>
          <div class="grid2">
            ${field(
              c,
              'lastDay',
              'Last day',
              html`<input id="lastDay" type="date" .value=${s.lastDay} max=${addDays(today, MAX_START_DAYS)}
                aria-invalid=${invalid(c, 'lastDay')} @input=${(e: Event) => {
                  s.lastDay = val(e);
                  touch(app, 'lastDay');
                  app.redraw();
                }}>`,
              { required: true, hint: `No more than ${MAX_START_DAYS} days from today (Central time). A past day is allowed.` },
            )}
            ${field(
              c,
              'terminationTime',
              'Access termination time',
              html`<select id="terminationTime" aria-invalid=${invalid(c, 'terminationTime')} @change=${(e: Event) => {
                s.terminationTime = val(e);
                touch(app, 'terminationTime');
                app.redraw();
              }}>
                ${TIMES.map((t) => html`<option value=${t} ?selected=${s.terminationTime === t}>${timeLabel(t)}</option>`)}
              </select>`,
              {
                required: true,
                hint: `Central time on the last day${isIsoDate(s.lastDay) ? `: ${instant(centralToUtc(s.lastDay, s.terminationTime))}` : ''}.`,
                soft,
              },
            )}
          </div>
          <div class="stack">
            ${switchRow(c, 'Grant mailbox access to someone?', s.emailAccessNeeded, (v) => {
              s.emailAccessNeeded = v;
              touch(app, 'emailAccessRecipient');
            })}
            ${s.emailAccessNeeded
              ? html`<div class="indent">${peoplePicker(c, 'emailWho', 'Email access recipient', s.emailWho, 'emailAccessRecipient', true,
                  `Who gets access to ${leaverName ? `${leaverName}'s` : 'the leaver’s'} mailbox.`)}</div>`
              : nothing}
            ${switchRow(c, 'Grant OneDrive access to someone?', s.onedriveAccessNeeded, (v) => {
              s.onedriveAccessNeeded = v;
              touch(app, 'onedriveAccessRecipient');
            })}
            ${s.onedriveAccessNeeded
              ? html`<div class="indent">${peoplePicker(c, 'onedriveWho', 'OneDrive access recipient', s.onedriveWho, 'onedriveAccessRecipient', true,
                  `Who gets access to ${leaverName ? `${leaverName}'s` : 'the leaver’s'} OneDrive files.`)}</div>`
              : nothing}
          </div>
        </section>

        <section class="card">
          <h3>Equipment to collect</h3>
          <fieldset class="field checks ${errorsFor('equipment').length ? 'has-err' : ''}">
            <legend>Equipment</legend>
            <p class="hint">Leave blank if nothing to collect.</p>
            <div class="opts">
              ${OFFBOARDING_EQUIPMENT.map(
                (x) => html`<label class="opt"><input type="checkbox" .checked=${s.equipment.has(x)} @change=${(e: Event) => {
                  checked(e) ? s.equipment.add(x) : s.equipment.delete(x);
                  touch(app, 'equipment');
                }}> ${EQUIPMENT_LABELS[x] ?? x}</label>`,
              )}
              <label class="opt"><input type="checkbox" .checked=${s.equipOther} @change=${(e: Event) => {
                s.equipOther = checked(e);
                touch(app, 'equipOtherDescription');
                app.redraw();
              }}> Other</label>
            </div>
            ${errorsFor('equipment').map((m) => html`<p class="ferr">${m}</p>`)}
          </fieldset>
          ${s.equipOther
            ? field(
                c,
                'equipOtherDescription',
                'Other equipment',
                html`<input id="equipOtherDescription" type="text" maxlength="255" .value=${s.equipOtherDescription}
                  aria-invalid=${invalid(c, 'equipOtherDescription')} @input=${(e: Event) => {
                    s.equipOtherDescription = val(e);
                    touch(app, 'equipOtherDescription');
                  }}>`,
                { required: true, hint: 'What else to collect.' },
              )
            : nothing}
          ${devicesPanel(s.devices)}
        </section>

        ${app.me.access.isSuperAdmin
          ? html`<section class="card admin-only">
              <h3>Super Admin</h3>
              ${switchRow(c, 'Skip 72-hr hold', s.forceStart, (v) => (s.forceStart = v),
                'Super Admin only: process this offboarding immediately (emergency termination) instead of holding it for the 72-hour undo window.')}
            </section>`
          : nothing}

        <div class="form-acts">
          <button class="btn" type="submit" ?disabled=${s.submitting}>${s.submitting ? 'Saving…' : 'Submit request'}</button>
          <a class="btn ghost" href="#/">Cancel</a>
        </div>
      </form>
      ${duplicateDialog(app)}
    </main>`;
}
